import { Box, Stack, Text, useMantineTheme } from '@mantine/core';
import { CartesianGrid, Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import type { TccData } from '../types';
import { latest, pctChange, previous } from '../helpers';
import { getMultiGoalStatus, getThresholdGoalState } from '../../statusUtils';
import { CardShell } from './CardShell';
import { MetricRow } from './MetricRow';

export function EmploymentCard({ data }: { data: TccData }) {
  const theme = useMantineTheme();
  const series = data.employment;
  const cur = series.length > 0 ? latest(series) : null;
  const prev = previous(series);

  const goalRate = 60;
  const employedAllTime = data.employmentAllTimeTotal;
  const gradsAllTime = data.graduatesAllTimeTotal;
  const employmentRateAllTime = gradsAllTime > 0 ? Math.round((employedAllTime / gradsAllTime) * 100) : 0;

  const qoq = cur ? pctChange(cur.employedWithinYear, prev?.employedWithinYear ?? null) : null;
  const arrow = (n: number): '↑' | '↓' | '' => (n > 0 ? '↑' : n < 0 ? '↓' : '');
  const qoqText = qoq === null ? '—' : arrow(qoq) ? `${arrow(qoq)}${Math.abs(qoq).toFixed(2)}% QoQ` : '0.00% QoQ';

  const parseQuarter = (label: string): { year: number; quarter: number } | null => {
    const match = label.match(/^Q([1-4])\s+(\d{4})$/);
    if (!match) return null;
    return { year: Number(match[2]), quarter: Number(match[1]) };
  };

  const currentMeta = parseQuarter(cur?.quarter ?? '');
  const ytd = currentMeta
    ? series
        .filter((point) => {
          const meta = parseQuarter(point.quarter);
          return meta?.year === currentMeta.year && meta.quarter <= currentMeta.quarter;
        })
        .reduce((sum, point) => sum + point.employedWithinYear, 0)
    : 0;

  const yoy = (() => {
    if (!currentMeta) return null;
    const prevYearTotal = series
      .filter((point) => {
        const meta = parseQuarter(point.quarter);
        return meta?.year === currentMeta.year - 1 && meta.quarter <= currentMeta.quarter;
      })
      .reduce((sum, point) => sum + point.employedWithinYear, 0);
    return pctChange(ytd, prevYearTotal);
  })();
  const yoyText = yoy === null ? '— YoY' : yoy === 0 ? '0.00% YoY' : `${arrow(yoy)}${Math.abs(yoy).toFixed(2)}% YoY`;

  const status = getMultiGoalStatus([
    getThresholdGoalState({ value: employmentRateAllTime, goal: goalRate, direction: 'min' }),
  ]);

  const tooltipShellStyle = {
    backgroundColor: theme.colorScheme === 'dark' ? '#000' : '#fff',
    border: `1px solid ${theme.colorScheme === 'dark' ? '#333' : '#ddd'}`,
    borderRadius: 10,
    color: theme.colorScheme === 'dark' ? '#fff' : '#000',
    padding: '10px 12px',
    maxWidth: 320,
    whiteSpace: 'normal',
    wordBreak: 'break-word',
  } as const;

  const seriesTotal = series.reduce((sum, point) => sum + point.employedWithinYear, 0);
  const priorEmployed = Math.max(0, employedAllTime - seriesTotal);
  let cumulative = priorEmployed;
  const chartData = series.map((point) => {
    cumulative += point.employedWithinYear;
    const employmentRate = gradsAllTime > 0 ? (cumulative / gradsAllTime) * 100 : 0;
    return {
      ...point,
      cumulative,
      employmentRate,
    };
  });
  const maxRate = Math.max(goalRate, ...chartData.map((p) => p.employmentRate));

  return (
    <CardShell
      title="Employment"
      subtitle={cur?.quarter ?? 'No data'}
      statusLabel={status.label}
      statusColor={status.color}
      statusTooltip={status.tooltip}
    >
      <Stack spacing="sm">
        <MetricRow
          items={[
            {
              label: 'Placed (This Quarter)',
              labelTooltip: 'Learners securing employment in the autonomous systems industry within 1 year of completion (count).',
              value: (
                <Text size="lg" weight={800}>
                  {cur?.employedWithinYear ?? '—'}
                </Text>
              ),
              sublabel: qoqText,
            },
            {
              label: 'Placed (YTD)',
              labelTooltip: 'Calendar year to date placements (sum of quarters in the current year through this quarter).',
              value: (
                <Text size="lg" weight={800}>
                  {ytd}
                </Text>
              ),
              sublabel: yoyText,
            },
            {
              label: 'Employment Rate (All-Time)',
              labelTooltip: `Placed ÷ Graduates (all-time). Goal: ${goalRate}%.`,
              value: (
                <Text size="lg" weight={800}>
                  {employmentRateAllTime}%
                </Text>
              ),
              sublabel: `Goal ${goalRate}%`,
            },
          ]}
        />

        <Text size="xs" color="dimmed" weight={700} sx={{ letterSpacing: 0.2, marginTop: '0.75rem' }}>
          Cumulative Employment Rate
        </Text>

        <Box sx={{ height: 170 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.18} vertical={false} />
              <XAxis dataKey="quarter" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} interval="preserveStartEnd" minTickGap={12} />
              <YAxis
                orientation="right"
                tick={{ fontSize: 11 }}
                allowDecimals={false}
                axisLine={false}
                tickLine={false}
                width={44}
                tickMargin={8}
                domain={[0, Math.min(100, Math.ceil(maxRate))]}
                tickFormatter={(value) => `${value}%`}
              />
              <Tooltip
                content={({ active, payload, label }) => {
                  if (!active || !payload || payload.length === 0) return null;
                  const p: any = payload[0]?.payload;
                  if (!p) return null;
                  return (
                    <div style={tooltipShellStyle as any}>
                      <div style={{ fontWeight: 700, marginBottom: 6, fontSize: 12, opacity: 0.9 }}>{label}</div>
                      <div style={{ fontSize: 12, lineHeight: 1.35 }}>
                        <div style={{ fontWeight: 900, fontSize: 14 }}>{Math.round(p.employmentRate)}% Employment Rate</div>
                        <div style={{ opacity: 0.9 }}>
                          {p.cumulative} placed out of {gradsAllTime} graduates
                        </div>
                      </div>
                    </div>
                  );
                }}
              />
              <ReferenceLine y={goalRate} stroke="#fa5252" strokeDasharray="4 4" ifOverflow="extendDomain" />
              <Line
                type="monotone"
                dataKey="employmentRate"
                name="Cumulative Employment Rate"
                stroke={theme.colors.blue[6]}
                strokeWidth={2}
                dot={(dotProps: any) => {
                  const pointKey = dotProps?.payload?.quarter ?? dotProps?.index ?? `${dotProps?.cx}-${dotProps?.cy}`;
                  return (
                    <circle
                      key={`tcc-employment-dot-${pointKey}`}
                      cx={dotProps.cx}
                      cy={dotProps.cy}
                      r={2}
                      fill={theme.colors.blue[6]}
                      stroke={theme.colorScheme === 'dark' ? 'rgba(0, 0, 0, 0.8)' : 'rgba(255, 255, 255, 0.8)'}
                      strokeWidth={1.5}
                    />
                  );
                }}
                activeDot={{ r: 4 }}
                isAnimationActive={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </Box>
      </Stack>
    </CardShell>
  );
}
